if (!customElements.get('slideshow-component')) {
  customElements.define('slideshow-component', class SlideshowComponent extends SliderComponent {
    constructor() {
      super();
      this.waitingVideo = null;
      this.waitingVideoEndedHandler = null;
      this.waitingVideoErrorHandler = null;
      this.waitingVideoMetadataHandler = null;
      this.autoplayVideoSlide = null;
      this.autoplayVideo = null;
      this.completedAutoplayVideo = null;
      this.failedAutoplayVideo = null;
      this.videoMediaQuery = null;
      this.videoMediaQueryChangeHandler = null;
      this.slideVisibilitySlider = null;
      this.slideVisibilityHandler = this.setSlideVisibility.bind(this);
    }

    init() {
      this.removeSliderInteractionListeners();
      this.resetVideoAutoplayState();
      super.init();
      this.setupSlideVideos();
      this.sliderControlWrapper = this.querySelector('.slider-buttons');
      this.enableSliderLooping = true;
      this.autoplayButtonQuerySelector = '.slideshow__autoplay';
      this.autoPlayPausedClass = 'slideshow__autoplay--paused';
  
      if (!this.sliderControlWrapper) return;
  
      if (this.sliderItemsToShow.length > 0) this.currentPage = 1;
  
      this.slideVisibilitySlider = this.slider;
      this.slideVisibilitySlider.addEventListener('scroll', this.slideVisibilityHandler);
      this.setSlideVisibility();

      this.querySelectorAll('.banner__price').forEach(ele => {
        ele.innerHTML = Shopify.showPrice(ele.dataset.price * 1);
      });
    }
  
    update() {
      const previousPage = this.currentPage;
      super.update();
      const activeSlide = this.querySelector('.slideshow__slide.active');
      if(activeSlide) activeSlide.classList.remove('active');
      this.querySelector(`.slideshow__slide:nth-child(${this.currentPage})`).classList.add('active');
      if (previousPage && previousPage !== this.currentPage && !this.autoplayButtonIsSetToPlay) {
        this.resetVideoAutoplayState();
      }
      if(this.prevButton) {
        this.prevButton.removeAttribute('disabled');
      }
    }
  
    setupSlideVideos() {
      if (this.videoMediaQuery && this.videoMediaQueryChangeHandler) {
        this.videoMediaQuery.removeEventListener('change', this.videoMediaQueryChangeHandler);
      }
      this.videoMediaQuery = null;
      this.videoMediaQueryChangeHandler = null;

      this.slideVideos = this.querySelectorAll('.slideshow__video');
      if (!this.slideVideos.length) return;
      this.slideVideos.forEach((video) => {
        // Guarantee muted inline playback so browsers allow autoplay (incl. iOS Safari).
        video.muted = true;
        video.setAttribute('playsinline', '');
        this.playVideo(video);
      });
      // Re-sync playback when the desktop/mobile media swaps (e.g. tablet rotation).
      this.videoMediaQuery = window.matchMedia('(min-width: 750px)');
      this.videoMediaQueryChangeHandler = this.syncVideoPlayback.bind(this);
      this.videoMediaQuery.addEventListener('change', this.videoMediaQueryChangeHandler);
    }

    syncVideoPlayback() {
      this.resetVideoAutoplayState();
      if (this.sliderControlWrapper && this.currentPage) {
        this.setSlideVisibility();
      } else {
        this.slideVideos.forEach((video) => {
          if (video.offsetParent === null) video.pause();
          else this.playVideo(video);
        });
      }
      if (this.isAutoplay && this.autoplayButtonIsSetToPlay) this.play();
    }

    playVideo(video) {
      // Skip videos hidden for the current viewport (e.g. small-hide when a mobile
      // image is shown instead) so they don't download/decode needlessly.
      if (video.offsetParent === null || video.paused === false || video.ended) return null;
      try {
        const playPromise = video.play();
        // force autoplay for safari
        if (playPromise !== undefined) playPromise.catch(() => {});
        return playPromise;
      } catch (error) {
        return false;
      }
    }

    setSlideVisibility() {
      this.sliderItemsToShow.forEach((item, index) => {
        const linkElements = item.querySelectorAll('a');
        const videos = item.querySelectorAll('.slideshow__video');
        if (index === this.currentPage - 1) {
          if (linkElements.length) linkElements.forEach(button => {
            button.removeAttribute('tabindex');
          });
          item.setAttribute('aria-hidden', 'false');
          item.removeAttribute('tabindex');
          videos.forEach((video) => {
            if (video.offsetParent === null) video.pause();
            else this.playVideo(video);
          });
        } else {
          if (linkElements.length) linkElements.forEach(button => {
            button.setAttribute('tabindex', '-1');
          });
          item.setAttribute('aria-hidden', 'true');
          item.setAttribute('tabindex', '-1');
          videos.forEach(video => video.pause());
        }
      });
    }

    cancelVideoWaitForNavigation() {
      clearTimeout(this.autoplay);
      if (!this.waitingVideo) return;
      this.completedAutoplayVideo = this.waitingVideo;
      this.clearVideoEndWait();
      this.pauseAutoplayProgress();
    }

    onButtonClick(event) {
      this.cancelVideoWaitForNavigation();
      super.onButtonClick(event);
    }

    linkToSlide(event) {
      const targetPage = this.sliderControlLinksArray.indexOf(event.currentTarget) + 1;
      if (targetPage !== this.currentPage) this.cancelVideoWaitForNavigation();
      super.linkToSlide(event);
    }

    getCurrentAutoplayVideo() {
      const currentSlide = this.sliderItemsToShow && this.sliderItemsToShow[this.currentPage - 1];
      let currentVideo = null;

      if (currentSlide && currentSlide.dataset.playVideoToEnd === 'true') {
        currentVideo = Array.from(currentSlide.querySelectorAll('.slideshow__video')).find((video) => video.offsetParent !== null) || null;
      }

      const selectionChanged = currentSlide !== this.autoplayVideoSlide || currentVideo !== this.autoplayVideo;
      if (selectionChanged) {
        this.clearVideoEndWait();
        this.autoplayVideoSlide = currentSlide;
        this.autoplayVideo = currentVideo;
        this.completedAutoplayVideo = null;
        this.failedAutoplayVideo = null;

        if (currentVideo) {
          try {
            currentVideo.currentTime = 0;
          } catch (error) {
            // The video will start at zero once its metadata is available.
          }
        }
      }

      return currentVideo === this.failedAutoplayVideo ? null : currentVideo;
    }

    waitForVideoEnd(video) {
      if (this.waitingVideo === video || this.completedAutoplayVideo === video) return;

      this.clearVideoEndWait();
      this.waitingVideo = video;
      this.waitingVideoEndedHandler = () => this.handleVideoEnded(video);
      this.waitingVideoErrorHandler = () => this.handleVideoWaitFailure(video);
      video.addEventListener('ended', this.waitingVideoEndedHandler, { once: true });
      video.addEventListener('error', this.waitingVideoErrorHandler, { once: true });
      video.addEventListener('abort', this.waitingVideoErrorHandler, { once: true });

      this.setVideoProgressDuration(video);
      this.restartAutoplayProgress();

      const playPromise = video.paused === false ? null : this.playVideo(video);
      if (playPromise === false) {
        this.handleVideoWaitFailure(video);
      } else if (playPromise !== null && playPromise !== undefined) {
        playPromise.catch(() => this.handleVideoWaitFailure(video));
      }

      if (video.error) {
        this.handleVideoWaitFailure(video);
      } else if (video.ended) {
        Promise.resolve().then(() => this.handleVideoEnded(video));
      }
    }

    handleVideoEnded(video) {
      if (this.waitingVideo !== video) return;
      const currentSlide = this.sliderItemsToShow && this.sliderItemsToShow[this.currentPage - 1];
      const shouldAdvance = currentSlide === this.autoplayVideoSlide
        && this.autoplayVideo === video
        && this.autoplayButtonIsSetToPlay;

      this.completedAutoplayVideo = video;
      this.clearVideoEndWait();
      this.pauseAutoplayProgress();
      if (shouldAdvance) this.autoRotateSlides();
    }

    handleVideoWaitFailure(video) {
      if (this.waitingVideo !== video) return;
      this.failedAutoplayVideo = video;
      this.clearVideoEndWait();
      this.play();
    }

    clearVideoEndWait() {
      if (this.waitingVideo) {
        if (this.waitingVideoEndedHandler) {
          this.waitingVideo.removeEventListener('ended', this.waitingVideoEndedHandler);
        }
        if (this.waitingVideoErrorHandler) {
          this.waitingVideo.removeEventListener('error', this.waitingVideoErrorHandler);
          this.waitingVideo.removeEventListener('abort', this.waitingVideoErrorHandler);
        }
        if (this.waitingVideoMetadataHandler) {
          this.waitingVideo.removeEventListener('loadedmetadata', this.waitingVideoMetadataHandler);
        }
      }
      this.waitingVideo = null;
      this.waitingVideoEndedHandler = null;
      this.waitingVideoErrorHandler = null;
      this.waitingVideoMetadataHandler = null;
    }

    resetVideoAutoplayState() {
      this.clearVideoEndWait();
      this.autoplayVideoSlide = null;
      this.autoplayVideo = null;
      this.completedAutoplayVideo = null;
      this.failedAutoplayVideo = null;
    }

    setVideoProgressDuration(video) {
      const updateDuration = () => {
        if (this.waitingVideo !== video) return;
        const remainingDuration = video.duration - video.currentTime;
        if (Number.isFinite(remainingDuration) && remainingDuration > 0) {
          this.setAutoplayProgressDuration(remainingDuration);
          this.restartAutoplayProgress();
        }
      };

      if (video.readyState > 0) {
        updateDuration();
      } else {
        this.setDefaultAutoplayProgressDuration();
        this.waitingVideoMetadataHandler = updateDuration;
        video.addEventListener('loadedmetadata', this.waitingVideoMetadataHandler, { once: true });
      }
    }

    setAutoplayProgressDuration(duration) {
      if (!this.sliderAutoplayButton) return;
      this.sliderAutoplayButton.style.setProperty('--speed', `${duration}s`);
    }

    setDefaultAutoplayProgressDuration() {
      const duration = this.autoplaySpeed ? this.autoplaySpeed / 1000 : Number(this.slider.dataset.speed);
      if (Number.isFinite(duration) && duration > 0) this.setAutoplayProgressDuration(duration);
    }

    restartAutoplayProgress() {
      if (!this.sliderAutoplayButton) return;
      this.sliderAutoplayButton.classList.remove(this.autoPlayPausedClass);
      const progressPath = this.sliderAutoplayButton.querySelector('.slideshow__progress__path');
      if (!progressPath) return;
      progressPath.getAnimations().forEach((animation) => {
        animation.cancel();
        animation.play();
      });
    }

    pauseAutoplayProgress() {
      if (!this.sliderAutoplayButton) return;
      const progressPath = this.sliderAutoplayButton.querySelector('.slideshow__progress__path');
      if (!progressPath) return;
      progressPath.getAnimations().forEach((animation) => animation.pause());
    }
  
    play() {
      const currentVideo = this.getCurrentAutoplayVideo();
      if (currentVideo) {
        this.slider.setAttribute('aria-live', 'off');
        clearTimeout(this.autoplay);
        this.waitForVideoEnd(currentVideo);
        if (this.sliderAutoplayButton) this.sliderAutoplayButton.classList.remove(this.autoPlayPausedClass);
      } else {
        this.clearVideoEndWait();
        this.setDefaultAutoplayProgressDuration();
        super.play();
        this.restartAutoplayProgress();
      }
    }

    pause() {
      this.clearVideoEndWait();
      super.pause();
      if(this.sliderAutoplayButton) {
        this.sliderAutoplayButton.classList.add(this.autoPlayPausedClass);
        this.pauseAutoplayProgress();
      }
    }

    disconnectedCallback() {
      this.resetVideoAutoplayState();
      clearTimeout(this.autoplay);
      if (this.videoMediaQuery && this.videoMediaQueryChangeHandler) {
        this.videoMediaQuery.removeEventListener('change', this.videoMediaQueryChangeHandler);
      }
      this.removeSliderInteractionListeners();
      super.disconnectedCallback();
    }

    removeSliderInteractionListeners() {
      if (!this.slideVisibilitySlider) return;
      this.slideVisibilitySlider.removeEventListener('scroll', this.slideVisibilityHandler);
      this.slideVisibilitySlider = null;
    }
  });
}
